import { ContractorType, CustomerList } from '@insurance/api/requests'
import { AddPersonButton } from '@insurance/components'
import { useValidationsField } from '@insurance/hooks'
import { PhoneInput, Text } from '@shared/components'
import { getFullName } from '@shared/utils'
import { Col, Flex, Form, Input, Row } from 'antd'
import { Add } from 'iconsax-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

type Props = {
  isEditable?: boolean
  customerKey?: string
  personsKey?: string
  sameContractorKey?: string
  contractorKey?: string
}

export const CustomerFields = ({
  isEditable,
  customerKey = 'customer',
  personsKey = 'persons',
  sameContractorKey = 'same_contractor_as_first_person',
  contractorKey = 'contractor'
}: Props) => {
  const { t } = useTranslation()
  const formInstance = Form.useFormInstance()
  const { required, fullName, phoneNumber } = useValidationsField()
  const customer = Form.useWatch(customerKey, { preserve: true })
  const contractor = Form.useWatch(contractorKey, { preserve: true })
  const sameContractorAsFirstPerson = Form.useWatch(sameContractorKey, {
    preserve: true
  })
  const firstPerson = Form.useWatch([personsKey, 0], {
    preserve: true
  })

  const handleAddClient = (customer?: CustomerList) => {
    formInstance.setFieldValue(customerKey, customer)
  }

  useEffect(() => {
    if (isEditable) return

    const isLegalEntity =
      contractor?.content_type === ContractorType.LEGALENTITY

    const person = sameContractorAsFirstPerson
      ? firstPerson
      : contractor?.person

    if (isLegalEntity) formInstance.setFieldValue(customerKey, contractor)
    else formInstance.setFieldValue(customerKey, person)
  }, [
    contractor,
    sameContractorAsFirstPerson,
    firstPerson,
    formInstance,
    isEditable,
    customerKey
  ])

  return (
    <Flex vertical gap={12}>
      <Flex
        align="center"
        gap={12}
        justify="space-between"
        style={{ paddingBottom: 12 }}
      >
        <Text size={16} strong>
          {t('client')}
        </Text>

        <AddPersonButton
          personType="client"
          type="link"
          icon={<Add size={16} />}
          getTitle={({ email, ...client }) => (
            <Text
              strong
              ellipsis={getFullName(client) ? false : { tooltip: email }}
            >
              {getFullName(client) || email}
            </Text>
          )}
          getSubtitle={({ email, ...client }) =>
            getFullName(client) && (
              <Text strong ellipsis={{ tooltip: email }}>
                {email}
              </Text>
            )
          }
          multiple={false}
          value={customer}
          onAdd={handleAddClient}
        >
          {t('add_existing_client')}
        </AddPersonButton>
      </Flex>

      <Row gutter={12}>
        <Col xs={24} lg={12}>
          <Form.Item
            name={[customerKey, 'last_name']}
            rules={[required, fullName]}
            label={t('last_name')}
          >
            <Input placeholder={t('last_name')} />
          </Form.Item>
        </Col>

        <Col xs={24} lg={12}>
          <Form.Item
            name={[customerKey, 'first_name']}
            rules={[required, fullName]}
            label={t('first_name')}
          >
            <Input placeholder={t('first_name')} />
          </Form.Item>
        </Col>

        <Col xs={24} lg={12}>
          <Form.Item
            name={[customerKey, 'phone']}
            rules={[phoneNumber]}
            label={t('phone')}
          >
            <PhoneInput placeholder={t('phone')} />
          </Form.Item>
        </Col>

        <Col xs={24} lg={12}>
          <Form.Item
            name={[customerKey, 'email']}
            label={t('email')}
            rules={[required, { type: 'email' }]}
          >
            <Input placeholder={t('email')} />
          </Form.Item>
        </Col>
      </Row>
    </Flex>
  )
}
